import { GameClientSocket } from "../networking/GameClientSocket";
import RemoteGameManager from "../runtime/managers/RemoteGameManager";
import {
  WantJoinPacket,
  PlayerWantJoinPacket,
  JoinResponsePacket,
  GameserverPacket,
  PlayerPacket,
  ServerStatePacket,
  GameMessagePacket,
  GameSettingsPacket,
  PlayerMovePacket,
  ClientMovePacket,
  PlayerStatePacket,
  GameStartPacket,
  GamePausePacket,
  ClientPausePacket,
  DeadPacket,
  ClientDeadPacket,
  ClientStatePacket,
  ClientRespawnPacket,
  RespawnPacket,
  PlayerLinesPacket,
  ClientLinesPacket,
  PlayerLeavePacket,
  ChangeTeamPacket,
  ClientStartWatchPacket,
  StampBlockPacket,
  ClientStampBlockPacket,
  DownloadStatePacket,
  PlayerStatsPacket,
  RejoinPacket,
  GonePacket,
  FirstFragPacket,
  ClientFirstFragPacket,
  LobbyUpdatePacket,
  ServerRandomPacket,
  EndgamePacket,
  Packet,
  PlayerCanvasStatePacket,
} from "../../core/protocol/packets";
import {
  getConnectionId,
  generatePlayerHashSync,
  storeGameConnection,
} from "../utils/playerIdentity";
import {
  PacketTypes,
  GameOptions,
  MoveType,
  PlayerMove,
  PlayerState,
  BoardState,
  GameState,
  Attack,
  GameMessageEvent,
} from "../../core/protocol/types";

/**
 * GameClient handles all client-side logic.
 * This class is responsible for:
 * - Connecting to host/server
 * - Sending player input and state updates
 * - Receiving and applying server state (non-authoritative)
 * - Local state management for display
 *
 * This class has NO dependencies on hosting logic or Game class.
 */
export class GameClient {
  private state: GameState;
  private socket: GameClientSocket;
  private updateCallback: (state: GameState) => void;
  private onJoinRejected?: (reason: string) => void;
  private countdownInterval?: NodeJS.Timeout;
  private readonly COUNTDOWN_RATE = 100;
  private readonly COUNTDOWN_INTERVAL_MS = 1000 / this.COUNTDOWN_RATE;
  private pendingPackets: PlayerLinesPacket[] = [];
  private lastSentState: Partial<PlayerState> | null = null;
  private lastStateSendTime: number = 0;
  private readonly STATE_SEND_INTERVAL = 50;
  private pendingStateUpdate: Partial<PlayerState> | null = null;
  /** Callback to queue packets to Game instance (matches original game->stackpacket()) */
  private queuePacketCallback?: (packet: Packet) => void;
  /** Host ID we're connected to (for sending packets to the correct peer) */
  private hostId?: string;
  /** Remote game manager for packet routing */
  private remoteGameManager?: RemoteGameManager;
  /** Track which players we're already watching (Set<numPlayer>) */
  private watchedPlayers: Set<number> = new Set();
  /** Queue for P_SERVERSTATE packets (matches original game->stackpacket()) */
  private queuedServerStatePackets: ServerStatePacket[] = [];
  /** Frame counter for P_SERVERSTATE processing delay (matches original syncframes) */
  private syncframes: number = 0;
  /** Pending player name for P_PLAYERWANTJOIN (set before sending P_WANTJOIN) */
  public pendingPlayerName?: string;
  /** Pending player team for P_PLAYERWANTJOIN (set before sending P_WANTJOIN) */
  public pendingPlayerTeam?: number;
  /** Pending P_DOWNLOAD packets for the local player until queue callback is ready */
  private pendingLocalDownloadPackets: DownloadStatePacket[] = [];

  constructor(
    socket: GameClientSocket,
    updateCallback: (state: GameState) => void,
    onJoinRejected?: (reason: string) => void,
    queuePacketCallback?: (packet: Packet) => void
  ) {
    this.socket = socket;
    this.updateCallback = updateCallback;
    this.onJoinRejected = onJoinRejected;
    this.queuePacketCallback = queuePacketCallback;
    this.state = this.createInitialState();

    this.socket.on(
      PacketTypes.JOIN_RESPONSE,
      this.handleJoinResponse
    );
    this.socket.on(
      PacketTypes.GAMESERVER,
      this.handleGameserver
    );
    this.socket.on(PacketTypes.PLAYER, this.handlePlayer);
    this.socket.on(
      PacketTypes.SERVER_STATE,
      this.handleServerState
    );
    this.socket.on(
      PacketTypes.GAME_MESSAGE,
      this.handleGameMessage
    );
    this.socket.on(
      PacketTypes.PLAYER_CANVAS_STATE,
      this.handlePlayerCanvasState
    );
    this.socket.on(
      PacketTypes.PLAYER_MOVE,
      this.handlePlayerMove
    );
    this.socket.on(
      PacketTypes.PLAYER_LEAVE,
      this.handlePlayerLeave
    );

    this.socket.on(
      PacketTypes.PLAYER_LINES,
      this.handlePlayerLines
    );
    this.socket.on(
      PacketTypes.GAME_START,
      this.handleGameStart
    );
    this.socket.on(PacketTypes.DEAD, this.handleDead);
    this.socket.on(
      PacketTypes.SERVER_STATE,
      this.handleServerState
    );
    this.socket.on(
      PacketTypes.STAMP_BLOCK,
      this.handleStampBlock
    );
    this.socket.on(
      PacketTypes.DOWNLOAD_STATE,
      this.handleDownloadState
    );
    this.socket.on(
      PacketTypes.PLAYER_STATS,
      this.handlePlayerStats
    );
    this.socket.on(PacketTypes.REJOIN, this.handleRejoin);
    this.socket.on(PacketTypes.GONE, this.handleGone);
    this.socket.on(
      PacketTypes.FIRST_FRAG,
      this.handleFirstFrag
    );
    this.socket.on(PacketTypes.RESPAWN, this.handleRespawn);
    this.socket.on(
      PacketTypes.LOBBY_UPDATE,
      this.handleLobbyUpdate
    );
    this.socket.on(PacketTypes.ENDGAME, this.handleEndgame);
    this.socket.on(
      PacketTypes.SERVER_RANDOM,
      this.handleServerRandom
    );
    this.socket.on(
      PacketTypes.GAME_PAUSE,
      this.handlePause
    );
  }

  /**
   * Set RemoteGameManager instance for packet routing
   */
  setRemoteGameManager(manager: RemoteGameManager): void {
    console.log(
      `[GameClient.setRemoteGameManager] Setting RemoteGameManager, existing games: ${
        this.remoteGameManager?.getAllRemoteGames().size || 0
      }, new manager will have: ${manager.getAllRemoteGames().size}`
    );

    if (
      this.remoteGameManager &&
      this.remoteGameManager.getAllRemoteGames().size > 0
    ) {
      console.log(
        `[GameClient.setRemoteGameManager] Migrating ${
          this.remoteGameManager.getAllRemoteGames().size
        } existing remote games to new manager`
      );
      const existingGames = this.remoteGameManager.getAllRemoteGames();
      for (const [numPlayer, entry] of existingGames.entries()) {
        manager.addRemoteGame(numPlayer, entry.game, entry.connectionId);
      }
    }
    this.remoteGameManager = manager;
    const currentMode = this.state.gameMode || "standard";
    this.remoteGameManager.setGameMode(currentMode);
    console.log(
      `[GameClient.setRemoteGameManager] RemoteGameManager set, now has ${
        this.remoteGameManager.getAllRemoteGames().size
      } games`
    );
  }

  /**
   * Join a game (connects and sends WANT_JOIN)
   */
  joinGame(hostId: string, playerName: string): Promise<void> {
    this.hostId = hostId;
    this.pendingPlayerName = playerName;
    // Default team 0, player can change later in lobby
    this.pendingPlayerTeam = 0;

    return this.socket.connect().then(() => {
      this.sendWantJoin();
    });
  }

  /**
   * Connect to a game server (establishes connection but doesn't send join request)
   * This allows the client to receive game state and see existing players before joining
   */
  connectToGame(hostId: string): Promise<void> {
    this.hostId = hostId;
    return this.socket.connect().then(() => {
      storeGameConnection(hostId);
    });
  }

  /**
   * Send P_WANTJOIN (initial join request, matches original game_menu.cc:409)
   * Reference: quadra/source/game_menu.cc:409 (Join_game sends P_WANTJOIN)
   * Server responds with P_GAMESERVER
   */
  sendWantJoin(): void {
    if (!this.hostId) {
      console.error(
        "[GameClient] Cannot send P_WANTJOIN: no hostId (not connected)"
      );
      if (this.onJoinRejected) {
        this.onJoinRejected("Not connected to a game server");
      }
      return;
    }

    const packet = new WantJoinPacket();

    packet.netVersion = 23;
    packet.language = 0;
    packet.os = 2;

    console.log(
      `[GameClient.sendWantJoin] Sending P_WANTJOIN to ${this.hostId}`
    );
    this.socket.send(packet);
  }

  /**
   * Send P_PLAYERWANTJOIN (player join request, matches original pane.cc:1733-1748)
   * Reference: quadra/source/pane.cc:1733 (Pane_playerjoin sends P_PLAYERWANTJOIN)
   * Server responds with P_PLAYERACCEPTED
   *
   * Packet structure matches original Packet_playerwantjoin (packets.h:216-232):
   * - team (uint8_t)
   * - name[40] (char)
   * - player (uint8_t, slot 0-2)
   * - repeat (int, -1 if net_version>=23, else h_repeat)
   * - smooth (int, 0 or 1)
   * - shadow (int, 0 or 1)
   * - handicap (int, 0-4)
   * - player_hash[16] (uint8_t)
   * - team_name[40] (char)
   * - team_hash[16] (uint8_t)
   * - h_repeat (int, 0-3)
   * - v_repeat (int, 0-3)
   */
  sendPlayerWantJoin(
    playerName: string,
    team: number,
    playerSlot: number = 0
  ): void {
    if (!this.hostId) {
      console.error(
        "[GameClient] Cannot send P_PLAYERWANTJOIN: no hostId (not connected)"
      );
      if (this.onJoinRejected) {
        this.onJoinRejected("Not connected to a game server");
      }
      return;
    }

    const connectionId = getConnectionId();
    const playerHash = generatePlayerHashSync(playerName, connectionId);

    const packet = new PlayerWantJoinPacket();
    packet.playerName = playerName;
    packet.playerTeam = team;
    packet.player = playerSlot;
    packet.playerHash = playerHash;

    const smoothRaw = localStorage.getItem("smooth");
    const shadowRaw = localStorage.getItem("shadow");
    const hRepeatRaw = localStorage.getItem("hRepeat");
    const vRepeatRaw = localStorage.getItem("vRepeat");

    packet.smooth = smoothRaw === "true" ? 1 : 0;
    packet.shadow = shadowRaw === "true" ? 1 : 0;
    packet.hRepeat = hRepeatRaw ? parseInt(hRepeatRaw) : 2;
    packet.vRepeat = vRepeatRaw ? parseInt(vRepeatRaw) : 2;

    const handicapRaw = localStorage.getItem("handicap");
    packet.handicap = handicapRaw ? parseInt(handicapRaw) : 0;

    const teamNameRaw = localStorage.getItem("teamName");
    packet.teamName = teamNameRaw || "";
    const teamHashRaw = localStorage.getItem("teamHash");
    packet.teamHash = teamHashRaw || "";

    const getPackPreference = (key: string): string => {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return "default";
      }
      try {
        const parsed = JSON.parse(raw);
        return typeof parsed === "string" ? parsed : raw;
      } catch {
        return raw;
      }
    };

    packet.blockPack = getPackPreference("blockPack");
    packet.canvasPack = getPackPreference("canvasPack");

    console.log(
      `[GameClient.sendPlayerWantJoin] Sending P_PLAYERWANTJOIN to ${this.hostId}`
    );
    this.socket.send(packet);
  }

  requestPauseToggle(): void {
    const packet = new ClientPausePacket();
    packet.senderId = this.state.localConnectionId || "";
    console.log(
      `[GameClient.requestPauseToggle] Sending P_CLIENTPAUSE (connectionId=${packet.senderId})`
    );
    this.socket.send(packet);
  }

  // Alias for requestPauseToggle to match call site expectation
  sendClientPause(): void {
    this.requestPauseToggle();
  }

  // Alias for handlePlayerInput to match call site expectation
  handleInput(keyCode: number): void {
    this.handlePlayerInput(keyCode);
  }

  handlePlayerInput(keyCode: number): void {
    if (this.state.isPaused) return;

    if (this.state.status === "starting" || this.state.status === "waiting")
      return;

    const moveType = this.keyCodeToMoveType(keyCode);
    if (moveType === null) return;

    const move: PlayerMove = {
      type: moveType,
      timestamp: Date.now(),
    };

    const serverMovePacket = new PlayerMovePacket();
    serverMovePacket.connectionId = this.state.localConnectionId;
    serverMovePacket.moves = [move];
    serverMovePacket.size = 1;
    this.applyLocalMove(serverMovePacket);
  }

  /**
   * Send moves to server (called by game loop, not user input)
   * Reference: quadra/source/canvas.cc:1098-1106 (send_p_moves)
   *
   * CRITICAL: Match original behavior - send_p_moves() sends immediately via net->sendtcp(moves)
   * The size check (>= 50) happens in Canvas.startByte() before calling sendPMoves(),
   * not here. This method always sends immediately, matching the original.
   * Reference: quadra/source/canvas.cc:1101 (net->sendtcp(moves) sends immediately)
   */
  sendMoves(moves: number[], numPlayer: number): void {
    if (moves.length === 0) {
      return;
    }

    this.sendMovePacket(moves, numPlayer);
  }

  /**
   * Internal method to send a move packet to the server
   */
  private sendMovePacket(movesToSend: number[], numPlayer: number): void {
    const playerMoves: PlayerMove[] = [];

    for (const moveByte of movesToSend) {
      const playerMove: PlayerMove = {
        type: MoveType.MOVE_DOWN,
        timestamp: Date.now(),
        rawByte: moveByte,
      };
      playerMoves.push(playerMove);
    }

    const movePacket = new ClientMovePacket();
    movePacket.connectionId = this.state.localConnectionId;
    movePacket.numPlayer = numPlayer;
    movePacket.moves = playerMoves;
    movePacket.size = playerMoves.length;

    this.socket.send(movePacket);
  }

  /**
   * Get all pending PlayerLinesPacket objects for batch processing.
   * This matches the original C++ pattern where all P_LINES packets are processed
   * synchronously in a batch before the game loop continues.
   * No lock needed - JavaScript is single-threaded, matching original synchronous processing.
   */
  getPendingPackets(): PlayerLinesPacket[] {
    const packets = [...this.pendingPackets];
    this.pendingPackets = [];
    return packets;
  }

  getPendingAttacks(): any[] {
    // Not implemented in GameClient yet, but kept for interface compatibility
    return [];
  }

  sendAttack(attack: Attack): void {
    if (!this.state.localConnectionId) {
      console.warn(
        "[GameClient] Cannot send attack: localConnectionId not set (not joined yet)"
      );
      return;
    }

    const sourcePlayer = this.getPlayerByConnectionId(
      this.state.localConnectionId
    );
    if (!sourcePlayer) {
      console.warn("[GameClient.sendAttack] Source player not found");
      return;
    }

    const sourceTeam = sourcePlayer.team;
    const sourceNumPlayer = sourcePlayer.numPlayer;

    const nb = attack.lines;
    const nc = attack.complexity;
    const lx = 127;
    const holePositions = attack.holePositions || [];

    this.state.players.forEach((player: PlayerState, numPlayer: number) => {
      if (!player) {
        return;
      }

      if (player.numPlayer === sourceNumPlayer) {
        return;
      }

      if (!player.isActive) {
        return;
      }

      if (sourceTeam !== undefined && player.team === sourceTeam) {
        return;
      }

      const packet = new ClientLinesPacket();
      packet.numPlayer = player.numPlayer;
      packet.nb = nb;
      packet.nc = nc;
      packet.lx = lx;
      packet.sender = sourceNumPlayer;
      packet.attack = {
        ...attack,
        sourceConnectionId: this.state.localConnectionId,
        targetConnectionId: player.connectionId,
      };
      packet.holePositions = holePositions;

      this.socket.send(packet);
    });
  }

  updatePlayerState(playerState: Partial<PlayerState>): void {
    if (!this.state.localConnectionId) return;

    const currentPlayer = this.getPlayerByConnectionId(
      this.state.localConnectionId
    );
    if (!currentPlayer) return;

    let updatedBoard = playerState.board;
    if (playerState.board && currentPlayer.board) {
      updatedBoard = {
        ...currentPlayer.board,
        ...playerState.board,
        currentPiece:
          playerState.board.currentPiece || currentPlayer.board.currentPiece,
      };
    }

    const {
      lastAttackerNumPlayer: _,
      attackCounts: __,
      ...playerStateWithoutAttackTracking
    } = playerState;
    const updatedPlayer = {
      ...currentPlayer,
      ...playerStateWithoutAttackTracking,
      board: updatedBoard || currentPlayer.board,
      lastAttackerNumPlayer: currentPlayer.lastAttackerNumPlayer,
      attackCounts: currentPlayer.attackCounts,
    };
    this.setPlayer(updatedPlayer);

    this.sendPlayerStateToServer(playerState);
    this.updateCallback(this.state);
  }

  notifyPlayerDeath(thenGone: boolean, lastAttackerNumPlayer?: number): void {
    console.log(
      `[GameClient.notifyPlayerDeath] Called: thenGone=${thenGone}, lastAttackerNumPlayer=${
        lastAttackerNumPlayer ?? "undefined"
      }`
    );

    if (!this.state.localConnectionId) {
      console.error(`[GameClient.notifyPlayerDeath] No localConnectionId!`);
      return;
    }

    const packet = new ClientDeadPacket();

    const localPlayer = this.getPlayerByConnectionId(
      this.state.localConnectionId
    );
    if (!localPlayer || localPlayer.numPlayer === undefined) {
      console.error(
        `[GameClient.notifyPlayerDeath] Local player not found or numPlayer not set! connectionId=${this.state.localConnectionId}`
      );
      return;
    }
    packet.numPlayer = localPlayer.numPlayer;
    packet.thenGone = thenGone;

    if (
      lastAttackerNumPlayer !== undefined &&
      lastAttackerNumPlayer >= 0 &&
      lastAttackerNumPlayer <= 5
    ) {
      packet.lastAttackerNumPlayer = lastAttackerNumPlayer;
      console.log(
        `[GameClient.notifyPlayerDeath] Setting lastAttackerNumPlayer=${lastAttackerNumPlayer}`
      );
    } else {
      console.log(
        `[GameClient.notifyPlayerDeath] No lastAttackerNumPlayer provided, frag message will not be created`
      );
    }

    console.log(
      `[GameClient.notifyPlayerDeath] Sending P_CLIENTDEAD: numPlayer=${
        packet.numPlayer
      }, thenGone=${packet.thenGone}, lastAttackerNumPlayer=${
        packet.lastAttackerNumPlayer ?? "none"
      }`
    );
    this.socket.send(packet);
  }

  // Alias for changeTeam to match call site expectation
  sendChangeTeam(newTeam: number): void {
    this.changeTeam(newTeam);
  }

  changeTeam(newTeam: number): void {
    if (!this.state.localConnectionId) return;

    const packet = new ChangeTeamPacket();
    packet.connectionId = this.state.localConnectionId;
    packet.newTeam = newTeam;
    this.socket.send(packet);
  }

  sendClientState(
    state: "playing" | "waitForWinner" | "waitForRestart" | "last"
  ): void {
    const packet = new ClientStatePacket();
    packet.connectionId = this.state.localConnectionId;
    const localPlayer = this.getPlayerByConnectionId(
      this.state.localConnectionId
    );
    packet.numPlayer = localPlayer?.numPlayer || 0;
    packet.state = state;
    this.socket.send(packet);
  }

  sendClientFirstFrag(): void {
    if (!this.state.localConnectionId) {
      return;
    }

    const packet = new ClientFirstFragPacket();
    packet.connectionId = this.state.localConnectionId;

    const localPlayer = this.getPlayerByConnectionId(
      this.state.localConnectionId
    );
    packet.numPlayer = localPlayer?.numPlayer || 0;

    this.socket.send(packet);
  }

  sendRespawnRequest(): void {
    if (!this.state.localConnectionId) {
      return;
    }
    const packet = new ClientRespawnPacket();
    packet.connectionId = this.state.localConnectionId;

    const localPlayer = this.getPlayerByConnectionId(
      this.state.localConnectionId
    );
    packet.numPlayer = localPlayer?.numPlayer || 0;

    this.socket.send(packet);
  }

  getState(): GameState {
    return this.state;
  }

  destroy(): void {
    this.stopCountdown();

    if (this.pendingStateUpdate) {
      this.flushPendingStateUpdate();
    }

    if (this.stateSendTimeout) {
      clearTimeout(this.stateSendTimeout);
      this.stateSendTimeout = undefined;
    }

    if (
      this.state.localConnectionId &&
      this.state.connections.has(this.state.localConnectionId)
    ) {
      const leavePacket = new PlayerLeavePacket();
      leavePacket.connectionId = this.state.localConnectionId;
      leavePacket.reason = "quit";
      leavePacket.announce = true;
      this.socket.send(leavePacket);
    }

    this.socket.disconnect();
  }


  /**
   * Set the callback to queue packets to Game instance (matches original game->stackpacket())
   */
  setQueuePacketCallback(callback: (packet: Packet) => void): void {
    this.queuePacketCallback = callback;
    if (
      this.pendingLocalDownloadPackets.length > 0 &&
      this.queuePacketCallback
    ) {
      console.log(
        `[GameClient.setQueuePacketCallback] Flushing ${this.pendingLocalDownloadPackets.length} pending local P_DOWNLOAD packets`
      );
      for (const pending of this.pendingLocalDownloadPackets) {
        this.queuePacketCallback(pending);
      }
      this.pendingLocalDownloadPackets = [];
    }
  }

  private handleJoinResponse = (packet: Packet): void => {
    const response = packet as JoinResponsePacket;

    if (response.acceptedCode !== 0 && response.acceptedCode !== 4) {
      console.error(
        `Join request rejected (code ${response.acceptedCode}): ${response.reason}`
      );

      this.socket.disconnect();

      if (this.onJoinRejected) {
        this.onJoinRejected(response.reason || "Join request rejected");
      }
      return;
    }

    this.state.localConnectionId = response.connectionId;
    if (response.hostConnectionId) {
        this.state.hostConnectionId = response.hostConnectionId;
    }
    this.state.gameSeed = response.gameSeed;

    if (response.playerId !== undefined) {
      this.state.localPlayerId = response.playerId;
    }

    if (response.rejoinPosition !== undefined) {
      this.state.localPlayerPosition = response.rejoinPosition;
    }

    const localPlayerPosition =
      response.rejoinPosition !== undefined ? response.rejoinPosition : 0;
    const localPlayerId = response.playerId || 0;

    let existingPlayerEntry:
      | { connectionId: string; player: PlayerState }
      | undefined;
    for (const [, player] of this.state.players.entries()) {
      if (
        player.playerId === localPlayerId ||
        player.numPlayer === localPlayerPosition
      ) {
        existingPlayerEntry = { connectionId: player.connectionId, player };
        break;
      }
    }

    if (existingPlayerEntry) {
      const oldConnectionId = existingPlayerEntry.connectionId;
      const updatedPlayer: PlayerState = {
        ...existingPlayerEntry.player,
        connectionId: response.connectionId,
        numPlayer: localPlayerPosition,
        playerId: localPlayerId,
        name: this.pendingPlayerName || existingPlayerEntry.player.name,
      };

      this.deletePlayerByConnectionId(oldConnectionId);
      this.setPlayer(updatedPlayer);
      console.log(
        `[GameClient.handleJoinResponse] Updated existing player entry: oldConnectionId=${oldConnectionId} -> newConnectionId=${response.connectionId}, numPlayer=${localPlayerPosition}, playerId=${localPlayerId}`
      );
    } else {
      const localPlayer: PlayerState = {
        connectionId: response.connectionId,
        name: this.pendingPlayerName || "Player",
        numPlayer: localPlayerPosition,
        team: 0,
        playerId: localPlayerId,
        board: this.createEmptyBoard(),
        score: 0,
        frags: 0,
        deaths: 0,
        isActive: true,
        state: "normal",
        level: 1,
        lines: 0,
        handicap: 0,
        isAttacking: false,
        fragAnnouncerPack: "default",
        blockPack: "default",
        canvasPack: "default",
        ppm: 0,
        bpm: 0,
      };
      this.setPlayer(localPlayer);
      console.log(
        `[GameClient.handleJoinResponse] Added new local player to state: connectionId=${response.connectionId}, numPlayer=${localPlayerPosition}, playerId=${localPlayerId}`
      );
    }

    if (response.gameParams) {
      this.state.params = {
        levelUp: response.gameParams.levelUp,
        startLevel: response.gameParams.startLevel,
        comboMin: response.gameParams.comboMin,
        allowHandicap: response.gameParams.allowHandicap,
        gameEndType: response.gameParams.gameEndType,
        gameEndValue: response.gameParams.gameEndValue,
      };
    }

    if (response.isRejoin && response.preservedStats) {
      const localPlayer = this.getPlayerByConnectionId(response.connectionId);
      if (localPlayer) {
        const updatedPlayer = {
          ...localPlayer,
          score: response.preservedStats.score,
          frags: response.preservedStats.frags,
          deaths: response.preservedStats.deaths,
          lines: response.preservedStats.lines,
          level: response.preservedStats.level,
        };
        this.setPlayer(updatedPlayer);
      }
      console.log(
        "[GameClient] Rejoined game with preserved stats:",
        response.preservedStats
      );
    }

    this.ensureWatchingAllPlayers();

    this.updateCallback(this.state);
  };

  private handlePlayerLeave = (packet: Packet): void => {
    const leavePacket = packet as PlayerLeavePacket;
    const connectionId = leavePacket.connectionId;

    if (!connectionId) return;
    this.deletePlayerByConnectionId(connectionId);
    this.updateCallback(this.state);
  };

  /**
   * Handle download state packet - sync board state for mid-game join
   * Matches original handling of P_DOWNLOAD
   */
  private handleDownloadState = (packet: Packet): void => {
    const downloadPacket = packet as DownloadStatePacket;

    console.log(
      "[GameClient] Received P_DOWNLOAD for player slot",
      downloadPacket.player,
      "players in state:",
      Array.from(this.state.players.entries()).map(
        ([id, p]) => `${id}:numPlayer=${p.numPlayer}`
      )
    );

    const targetNumPlayer = downloadPacket.player;
    const targetPlayerState = this.getPlayerByNumPlayer(targetNumPlayer);

    if (
      !targetPlayerState ||
      targetPlayerState.isGone ||
      targetPlayerState.isDedicatedServer
    ) {
      console.warn(
        `[GameClient] Could not find active player for slot ${targetNumPlayer}. Players in state:`,
        Array.from(this.state.players.entries()).map(
          ([id, p]) =>
            `${id}:numPlayer=${p.numPlayer}, connectionId=${p.connectionId}, isGone=${p.isGone}, isDedicatedServer=${p.isDedicatedServer}`
        )
      );
      return;
    }

    const targetConnectionId = targetPlayerState.connectionId;
    const isLocalPlayer =
      targetConnectionId === this.state.localConnectionId &&
      !!this.state.localConnectionId;

    console.log(
      `[GameClient] Routing P_DOWNLOAD for slot ${targetNumPlayer} (connectionId=${targetConnectionId})`
    );

    if (isLocalPlayer) {
      if (this.queuePacketCallback) {
        console.log(
          `[GameClient] Routing P_DOWNLOAD to local game (numPlayer=${targetNumPlayer}) - will be handled via watchers, not queued`
        );
        this.queuePacketCallback(downloadPacket);
      } else {
        console.warn(
          `[GameClient] P_DOWNLOAD for local player (numPlayer=${targetNumPlayer}) but queuePacketCallback not set yet. Local game may not be initialized.`
        );
        this.pendingLocalDownloadPackets.push(downloadPacket);
      }
    } else {
      if (!this.remoteGameManager) {
        this.remoteGameManager = new RemoteGameManager();
        console.log(
          `[GameClient.handleDownloadState] Created RemoteGameManager on-demand for P_DOWNLOAD`
        );
      }

      let remoteGame =
        this.remoteGameManager.getRemoteGameByNumPlayer(targetNumPlayer);
      if (!remoteGame) {
        if (!this.state.gameSeed) {
          console.warn(
            `[GameClient] Cannot create remote game: gameSeed not available yet for numPlayer=${targetNumPlayer}`
          );
          return;
        }

        console.log(
          `[GameClient] Creating remote game on-demand for numPlayer=${targetNumPlayer} from P_DOWNLOAD`
        );
        remoteGame = this.remoteGameManager.createOrUpdateRemoteGame(
          targetConnectionId || `player_${targetNumPlayer}`,
          targetPlayerState,
          this.state.gameSeed,
          true,
          this.state.gameMode
        );
      }

      console.log(
        `[GameClient] Routing P_DOWNLOAD to remote game for numPlayer=${targetNumPlayer} (connectionId=${targetConnectionId}, waitDownload=${remoteGame.canvas.waitDownload})`
      );
      this.remoteGameManager.processPacketForPlayer(
        targetNumPlayer,
        downloadPacket
      );
    }

    this.updateCallback(this.state);
  };

  /**
   * Handle player stats packet - sync stats for mid-game join
   * Matches original handling of P_STAT
   * Stats array format: [score, lines, frags, deaths, level, ppm, bpm]
   */
  private handlePlayerStats = (packet: Packet): void => {
    const statsPacket = packet as PlayerStatsPacket;

    let player: PlayerState | undefined;
    for (const [, p] of this.state.players.entries()) {
      if (p.numPlayer === statsPacket.player && !p.isGone) {
        player = p;
        break;
      }
    }

    if (!player) {
      console.warn(
        `[GameClient.handlePlayerStats] Player not found: numPlayer=${statsPacket.player}`
      );
      return;
    }

    if (statsPacket.stats.length < 5) {
      console.warn(
        `[GameClient.handlePlayerStats] Invalid stats array length: ${statsPacket.stats.length} (expected at least 5)`
      );
      return;
    }

    const updatedPlayer: PlayerState = {
      ...player,
      score: statsPacket.stats[0] ?? player.score,
      lines: statsPacket.stats[1] ?? player.lines,
      frags: statsPacket.stats[2] ?? player.frags ?? 0,
      deaths: statsPacket.stats[3] ?? player.deaths ?? 0,
      level: statsPacket.stats[4] ?? player.level,
      ppm: statsPacket.stats[5] ?? player.ppm ?? 0,
      bpm: statsPacket.stats[6] ?? player.bpm ?? 0,
    };

    this.setPlayer(updatedPlayer);
    this.updateCallback(this.state);
  };

  /**
   * Handle rejoin packet - player rejoined the game
   * Matches original handling of P_REJOIN
   */
  private handleRejoin = (packet: Packet): void => {
    const rejoinPacket = packet as RejoinPacket;
    console.log(
      "[GameClient] Player",
      rejoinPacket.player,
      "rejoined the game"
    );
  };

  /**
   * Handle gone packet - player disconnected
   * Matches original handling of P_GONE
   */
  private handleFirstFrag = (packet: Packet): void => {
    const fragPacket = packet as FirstFragPacket;

    console.log(
      `[GameClient.handleFirstFrag] Received P_FIRST_FRAG: numPlayer=${fragPacket.numPlayer}, connectionId=${fragPacket.connectionId}`
    );

    if (this.remoteGameManager) {
      console.log(
        `[GameClient.handleFirstFrag] Routing P_FIRST_FRAG to remote game for numPlayer=${fragPacket.numPlayer}`
      );
      this.remoteGameManager.processPacketForPlayer(
        fragPacket.numPlayer,
        fragPacket
      );
    }

    let localPlayer: PlayerState | undefined;
    for (const [, player] of this.state.players.entries()) {
      if (player.connectionId === this.state.localConnectionId) {
        localPlayer = player;
        break;
      }
    }
    if (localPlayer && localPlayer.numPlayer === fragPacket.numPlayer) {
      if (this.queuePacketCallback) {
        this.queuePacketCallback(fragPacket);
      } else {
        console.warn(
          "[GameClient] FirstFragPacket received but no queuePacketCallback set"
        );
      }
    }
  };

  private handleRespawn = (packet: Packet): void => {
    const respawnPacket = packet as RespawnPacket;

    console.log(
      `[GameClient.handleRespawn] Received P_RESPAWN: numPlayer=${respawnPacket.numPlayer}, connectionId=${respawnPacket.connectionId}`
    );

    if (this.remoteGameManager) {
      console.log(
        `[GameClient.handleRespawn] Routing P_RESPAWN to remote game for numPlayer=${respawnPacket.numPlayer}`
      );
      this.remoteGameManager.processPacketForPlayer(
        respawnPacket.numPlayer,
        respawnPacket
      );
    }

    const player = this.getPlayerByConnectionId(respawnPacket.connectionId);
    if (!player) {
      return;
    }

    const startLevel = this.state.params?.startLevel || 1;
    const resetPlayer: PlayerState = {
      ...player,
      isActive: true,
      isGone: false,
      state: "normal",
      canvasState: "playing",
      board: this.createEmptyBoard(),
      lines: 0,
      level: startLevel,
      lastAttackerNumPlayer: undefined,
      attackCounts: {},
      ppm: 0,
      bpm: 0,
    };

    this.setPlayer(resetPlayer);
    this.updateCallback(this.state);
  };

  private handleGone = (packet: Packet): void => {
    const gonePacket = packet as GonePacket;
    const player = this.getPlayerByConnectionId(gonePacket.connectionId);
    if (player) {
      const updatedPlayer = {
        ...player,
        isGone: true,
        isActive: false,
      };
      this.setPlayer(updatedPlayer);
      this.updateCallback(this.state);
    }
  };

  /**
   * Handle incoming PlayerLinesPacket - just queue it for batch processing.
   * This matches the original C++ pattern where packets are queued and then
   * processed synchronously in a batch in the game loop.
   * No lock needed - JavaScript is single-threaded, matching original synchronous processing.
   */
  private handlePlayerLines = (packet: Packet): void => {
    const linesPacket = packet as PlayerLinesPacket;

    console.log(
      `[GameClient.handlePlayerLines] Received PLAYER_LINES: numPlayer=${linesPacket.numPlayer}, sender=${linesPacket.sender}, nb=${linesPacket.nb}, nc=${linesPacket.nc}`
    );

    const targetPlayer = this.getPlayerByNumPlayer(linesPacket.numPlayer);

    if (!targetPlayer) {
      console.warn(
        `[GameClient.handlePlayerLines] No player found with numPlayer=${linesPacket.numPlayer}`
      );
      return;
    }

    if (targetPlayer.connectionId === this.state.localConnectionId) {
      this.pendingPackets.push(linesPacket);
    } else {
      if (!this.remoteGameManager) {
        console.warn(
          `[GameClient] No remoteGameManager! Cannot route ${linesPacket.attack.lines} lines to numPlayer=${linesPacket.numPlayer}`
        );
        return;
      }
      this.remoteGameManager.processPacketForPlayer(
        linesPacket.numPlayer,
        linesPacket
      );
    }
  };

  /**
   * Handle P_GAMESERVER packet (initial join, matches original game.cc:112-167)
   * Reference: quadra/source/game.cc:112 (Game::Game(const Packet_gameserver *p))
   */
  private handleGameserver = (packet: Packet): void => {
    const gameserverPacket = packet as GameserverPacket;

    this.state.gameSeed = gameserverPacket.gameSeed;
    this.state.isPaused = gameserverPacket.paused;
    this.state.gameMode = gameserverPacket.survivor
      ? "survivor"
      : gameserverPacket.hotPotato
      ? "hot_potato"
      : "standard";
    this.state.hostConnectionId = gameserverPacket.hostConnectionId;
    this.state.roundSyncState = gameserverPacket.syncpoint;

    if (gameserverPacket.delayStart !== undefined) {
      this.state.countdown = gameserverPacket.delayStart;
      if (
        gameserverPacket.delayStart &&
        gameserverPacket.delayStart !== 0 &&
        gameserverPacket.delayStart !== 500
      ) {
        this.startCountdown(gameserverPacket.delayStart);
      } else {
        this.stopCountdown();
        if (gameserverPacket.delayStart === 500) {
          this.state.status = "waiting";
          this.state.isPaused = true;
        }
      }
    }

    if (!this.state.params) {
      let gameEndType: "none" | "frag" | "time" | "lines" | "score" = "none";
      if (gameserverPacket.gameEnd === 1) {
        gameEndType = "frag";
      } else if (gameserverPacket.gameEnd === 2) {
        gameEndType = "time";
      } else if (gameserverPacket.gameEnd === 3) {
        gameEndType = "lines";
      } else if (gameserverPacket.gameEnd === 4) {
        gameEndType = "score";
      }

      this.state.params = {
        levelUp: !gameserverPacket.noLevelUp,
        startLevel: gameserverPacket.levelStart,
        comboMin: gameserverPacket.comboMin,
        allowHandicap: gameserverPacket.allowHandicap,
        gameEndType,
        gameEndValue: gameserverPacket.gameEndValue,
      };
    } else {
      this.state.params.levelUp = !gameserverPacket.noLevelUp;
      this.state.params.startLevel = gameserverPacket.levelStart;
      this.state.params.comboMin = gameserverPacket.comboMin;
      this.state.params.allowHandicap = gameserverPacket.allowHandicap;

      if (gameserverPacket.gameEnd === 1) {
        this.state.params.gameEndType = "frag";
      } else if (gameserverPacket.gameEnd === 2) {
        this.state.params.gameEndType = "time";
      } else if (gameserverPacket.gameEnd === 3) {
        this.state.params.gameEndType = "lines";
      } else if (gameserverPacket.gameEnd === 4) {
        this.state.params.gameEndType = "score";
      } else {
        this.state.params.gameEndType = "none";
      }
      this.state.params.gameEndValue = gameserverPacket.gameEndValue;
    }

    const localPlayerBeforeUpdate = this.getPlayerByConnectionId(
      this.state.localConnectionId
    );
    const preservedLastAttackerNumPlayer =
      localPlayerBeforeUpdate?.lastAttackerNumPlayer;
    const preservedAttackCounts = localPlayerBeforeUpdate?.attackCounts;

    const serverPlayerIds = new Set(
      gameserverPacket.players.map((p: any) => p.playerId)
    );
    for (const [numPlayer, player] of this.state.players.entries()) {
      if (
        player.connectionId !== this.state.localConnectionId &&
        player.playerId &&
        !serverPlayerIds.has(player.playerId)
      ) {
        this.deletePlayerByNumPlayer(numPlayer);
      }
    }

    const localPlayerId = this.state.localPlayerId;
    const localPlayerPosition = this.state.localPlayerPosition;
    const localConnectionId = this.state.localConnectionId;

    for (const p of gameserverPacket.players) {
      let existingPlayer: PlayerState | undefined;
      let existingConnectionId: string | undefined;

      existingPlayer = this.getPlayerByNumPlayer(p.quel);
      if (existingPlayer) {
        existingConnectionId = existingPlayer.connectionId;
      } else {
        for (const [, player] of this.state.players.entries()) {
          if (player.playerId === p.playerId) {
            existingPlayer = player;
            existingConnectionId = player.connectionId;
            break;
          }
        }
      }

      const isLocalPlayer =
        (localPlayerId !== undefined && p.playerId === localPlayerId) ||
        (localPlayerPosition !== undefined && p.quel === localPlayerPosition) ||
        existingConnectionId === localConnectionId ||
        (localConnectionId &&
          !existingPlayer &&
          this.state.isServer &&
          (gameserverPacket.players.length === 1 || p.quel === 0));

      console.log(
        `[GameClient.handleGameserver] Player ${p.quel} (playerId=${p.playerId}, name=${p.name}): existingConnectionId=${existingConnectionId}, isLocalPlayer=${isLocalPlayer}`
      );

      if (existingPlayer && existingConnectionId) {
        const isLocal = existingConnectionId === this.state.localConnectionId;
        if (isLocal) {
          console.log(
            `[GameClient.handleGameserver] Updating local player team: ${existingPlayer.team} -> ${p.team}, connectionId=${existingConnectionId}, localConnectionId=${this.state.localConnectionId}`
          );
        }
        const updatedPlayer = {
          ...existingPlayer,
          numPlayer: p.quel,
          team: p.team,
          name: p.name,
          playerId: p.playerId,
          handicap: p.handicap,
          blockPack: p.blockPack || existingPlayer.blockPack || "default",
          canvasPack: p.canvasPack || existingPlayer.canvasPack || "default",
          fragAnnouncerPack:
            p.fragAnnouncerPack ||
            existingPlayer.fragAnnouncerPack ||
            "default",

          lastAttackerNumPlayer: isLocal
            ? preservedLastAttackerNumPlayer
            : existingPlayer.lastAttackerNumPlayer,
          attackCounts: isLocal
            ? preservedAttackCounts
            : existingPlayer.attackCounts,
        };
        this.setPlayer(updatedPlayer);
      } else {
        const connectionId =
          isLocalPlayer && localConnectionId
            ? localConnectionId
            : `player_${p.playerId}_${p.quel}`;

        const newPlayer: PlayerState = {
          connectionId: connectionId,
          name: p.name,
          numPlayer: p.quel,
          team: p.team,
          playerId: p.playerId,
          handicap: p.handicap,
          blockPack: p.blockPack || "default",
          canvasPack: p.canvasPack || "default",
          fragAnnouncerPack: p.fragAnnouncerPack || "default",
          board: this.createEmptyBoard(),
          score: 0,
          isActive: true,
          state: "normal",
          level: gameserverPacket.levelStart,
          lines: 0,
          isAttacking: false,
          ppm: 0,
          bpm: 0,
        };
        this.setPlayer(newPlayer);
      }
    }

    if (gameserverPacket.gameSeed !== undefined) {
      if (this.remoteGameManager) {
        const mode = this.state.gameMode || "standard";
        this.remoteGameManager.setGameMode(mode);
      }
      if (!this.remoteGameManager) {
        this.remoteGameManager = new RemoteGameManager();
        console.log(
          `[GameClient.handleGameserver] Created RemoteGameManager synchronously (matches original Canvas creation)`
        );
      }

      for (const [numPlayer, playerState] of this.state.players.entries()) {
        if (
          playerState.connectionId !== this.state.localConnectionId &&
          !playerState.isDedicatedServer &&
          playerState.connectionId
        ) {
          const existingGame =
            this.remoteGameManager?.getRemoteGameByNumPlayer(numPlayer);
          if (!existingGame) {
            const waitDownload = true;
            const mode = this.state.gameMode || "standard";
            this.remoteGameManager.createOrUpdateRemoteGame(
              playerState.connectionId,
              playerState,
              gameserverPacket.gameSeed,
              waitDownload,
              mode
            );
          }
        }
      }
    }

    console.log(
      `[GameClient.handleGameserver] After processing players, state has ${this.state.players.size} players:`,
      Array.from(this.state.players.keys())
    );

    if (this.pendingPlayerName && this.pendingPlayerTeam !== undefined) {
      this.sendPlayerWantJoin(this.pendingPlayerName, this.pendingPlayerTeam);
      this.pendingPlayerName = undefined;
      this.pendingPlayerTeam = undefined;
    }

    this.ensureWatchingAllPlayers();

    this.updateCallback(this.state);
  };

  /**
   * Handle P_LOBBY_UPDATE (lobby updates: team changes, game end settings)
   * Updates only lobby state without triggering game initialization
   */
  private handleLobbyUpdate = (packet: Packet): void => {
    const lobbyPacket = packet as LobbyUpdatePacket;
    if (lobbyPacket.hostConnectionId) {
        this.state.hostConnectionId = lobbyPacket.hostConnectionId;
    }

    console.log(
      `[GameClient.handleLobbyUpdate] Received P_LOBBY_UPDATE: players=${lobbyPacket.players.length}, gameEnd=${lobbyPacket.gameEnd}, gameEndValue=${lobbyPacket.gameEndValue}`
    );

    for (const p of lobbyPacket.players) {
      let existingPlayer: PlayerState | undefined;
      let existingConnectionId: string | undefined;
      for (const [, player] of this.state.players.entries()) {
        if (player.playerId === p.playerId || player.numPlayer === p.quel) {
          existingPlayer = player;
          existingConnectionId = player.connectionId;
          break;
        }
      }

      if (existingPlayer && existingConnectionId) {
        const updatedPlayer = {
          ...existingPlayer,
          team: p.team,
          name: p.name,
          handicap: p.handicap,
        };
        this.setPlayer(updatedPlayer);

        if (this.remoteGameManager) {
            this.remoteGameManager.updatePlayerTeam(p.quel, p.team);
        }
      }
    }

    if (this.state.params) {
      if (lobbyPacket.gameEnd === 1) {
        this.state.params.gameEndType = "frag";
      } else if (lobbyPacket.gameEnd === 2) {
        this.state.params.gameEndType = "time";
      } else if (lobbyPacket.gameEnd === 3) {
        this.state.params.gameEndType = "lines";
      } else if (lobbyPacket.gameEnd === 4) {
        this.state.params.gameEndType = "score";
      } else {
        this.state.params.gameEndType = "none";
      }
      this.state.params.gameEndValue = lobbyPacket.gameEndValue;
    }

    this.updateCallback(this.state);
  };

  /**
   * Handle P_ENDGAME packet (matches original check_end_game() client side)
   * Reference: quadra/source/net_list.cc:746-761
   * Client receives P_ENDGAME and calls game->endgame() which sets terminated=true
   */
  private handleEndgame = (packet: Packet): void => {
    const endgamePacket = packet as EndgamePacket;

    console.log(
      `[GameClient.handleEndgame] Received P_ENDGAME: autoEnd=${
        endgamePacket.autoEnd
      }, queuePacketCallback=${!!this.queuePacketCallback}`
    );

    if (this.queuePacketCallback) {
      console.log(`[GameClient.handleEndgame] Queuing P_ENDGAME to game`);
      this.queuePacketCallback(endgamePacket);
    } else {
      console.warn(
        "[GameClient.handleEndgame] No queuePacketCallback set, cannot queue P_ENDGAME"
      );
    }
  };

  /**
   * Automatically send CLIENT_START_WATCH for all players we're not already watching
   * This ensures we receive move packets from all players
   * Called from handleGameState (same place as P_PLAYER handling in original net_list.cc:1361)
   * Reference: quadra/source/pane.cc:2054-2061 (Watch_canvas::start)
   */
  public ensureWatchingAllPlayers(): void {
    console.log(
      `[GameClient.ensureWatchingAllPlayers] Called, state has ${this.state.players.size} players`
    );

    const localConnectionId =
      this.state.localConnectionId;

    if (!localConnectionId) {
      console.log(
        `[GameClient.ensureWatchingAllPlayers] No localConnectionId yet, skipping`
      );
      return;
    }

    console.log(
      `[GameClient.ensureWatchingAllPlayers] Looking for local player with connectionId=${localConnectionId}`
    );
    const localPlayer = this.getPlayerByConnectionId(localConnectionId);
    if (!localPlayer) {
      console.log(
        `[GameClient.ensureWatchingAllPlayers] Local player not found in state (localConnectionId=${localConnectionId}), players:`,
        Array.from(this.state.players.keys())
      );
      return;
    }

    console.log(
      `[GameClient.ensureWatchingAllPlayers] Found local player: numPlayer=${localPlayer.numPlayer}, name=${localPlayer.name}`
    );

    const currentPlayerNums = new Set<number>();

    let watchedCount = 0;
    for (const [, player] of this.state.players.entries()) {
      if (
        player.connectionId === localConnectionId ||
        player.isGone ||
        player.isDedicatedServer
      ) {
        console.log(
          `[GameClient.ensureWatchingAllPlayers] Skipping player ${
            player.numPlayer
          } (${player.name}): isLocal=${
            player.connectionId === localConnectionId
          }, isGone=${player.isGone}, isDedicatedServer=${
            player.isDedicatedServer
          }`
        );
        continue;
      }

      currentPlayerNums.add(player.numPlayer);

      if (!this.watchedPlayers.has(player.numPlayer)) {
        const watchPacket = new ClientStartWatchPacket();
        watchPacket.numPlayer = player.numPlayer;
        watchPacket.stop = false;
        watchPacket.update = 60;

        console.log(
          `[GameClient] Auto-watching player ${player.numPlayer} (${player.name}, connectionId=${player.connectionId})`
        );
        this.socket.send(watchPacket);
        this.watchedPlayers.add(player.numPlayer);
        watchedCount++;
        console.log(`[GameClient] Sent CLIENT_START_WATCH packet:`, {
          numPlayer: watchPacket.numPlayer,
          stop: watchPacket.stop,
          update: watchPacket.update,
          packetId: watchPacket.packetId,
        });
      } else {
        console.log(
          `[GameClient.ensureWatchingAllPlayers] Already watching player ${player.numPlayer} (${player.name})`
        );
      }
    }

    console.log(
      `[GameClient.ensureWatchingAllPlayers] Complete: started watching ${watchedCount} new players, total watched: ${this.watchedPlayers.size}`
    );

    for (const numPlayer of this.watchedPlayers) {
      if (!currentPlayerNums.has(numPlayer)) {
        const watchPacket = new ClientStartWatchPacket();
        watchPacket.numPlayer = numPlayer;
        watchPacket.stop = true;

        console.log(
          `[GameClient] Auto-stop watching player ${numPlayer} (player left)`
        );
        this.socket.send(watchPacket);
        this.watchedPlayers.delete(numPlayer);
      }
    }
  }

  private handlePlayerMove = (packet: Packet): void => {
    const movePacket = packet as PlayerMovePacket;

    if (movePacket.connectionId !== this.state.localConnectionId) {
      this.applyMove(movePacket);

      if (this.remoteGameManager) {
        this.remoteGameManager.queueMovePacketForPlayer(
          movePacket.numPlayer,
          movePacket
        );
      } else {
        console.warn(
          `[GameClient.handlePlayerMove] No remoteGameManager! Cannot queue moves for numPlayer=${movePacket.numPlayer}`
        );
      }
    }
  };

  private applyMove(movePacket: PlayerMovePacket): void {
    let player: PlayerState | undefined;
    for (const [, p] of this.state.players.entries()) {
      if (
        p.numPlayer === movePacket.numPlayer &&
        p.connectionId !== this.state.localConnectionId
      ) {
        player = p;
        break;
      }
    }
    if (!player) return;

    movePacket.moves.forEach((move: any) => {
      switch (move.type) {
        case MoveType.MOVE_LEFT:
          break;
        case MoveType.MOVE_RIGHT:
          break;
        case MoveType.MOVE_DOWN:
          break;
        case MoveType.DROP:
          break;
        case MoveType.ROTATE_CW:
          break;
        case MoveType.ROTATE_CCW:
          break;
        case MoveType.HOLD:
          break;
      }
    });

    this.updateCallback(this.state);
  }

  private applyLocalMove(movePacket: PlayerMovePacket): void {
    const player = this.getPlayerByConnectionId(this.state.localConnectionId);
    if (!player) return;

    this.applyMove(movePacket);
  }

  private handleGameStart = (packet: Packet): void => {
    const startPacket = packet as GameStartPacket;

    this.state.gameSeed = startPacket.gameSeed;
    this.state.isPaused = true;
    this.startCountdown(499);
  };

  /**
   * Handle pause packet from server (matches original Net_client::pause)
   * Reference: quadra/source/net_server.cc:122-169
   * Special logic: if paused and delay_start==500, starts countdown
   */
  private handlePause = (packet: Packet): void => {
    const pausePacket = packet as GamePausePacket;

    console.log(
      `[GameClient.handlePause] Received P_PAUSE: player=${pausePacket.player}, delayStart=${pausePacket.delayStart}, currentPaused=${this.state.isPaused}, countdown=${this.state.countdown}`
    );

    // Check for server start signal (delayStart = 500)
    if (pausePacket.player === -1 && pausePacket.delayStart === 500) {
        console.log("[GameClient.handlePause] Received start countdown signal");
        this.startCountdown(499);
        return;
    }

    if (this.state.isPaused) {
      if (!this.state.countdown || this.state.countdown === 0) {
        console.log("[GameClient.handlePause] Unpausing game");
        this.state.isPaused = false;
        this.state.status = "active";
        this.stopCountdown();
        this.updateCallback(this.state);
        return;
      }

      if (this.state.countdown === 500) {
        // Only start countdown if explicitly signaled by server
        if (pausePacket.delayStart === 500) {
          console.log(
            `[GameClient.handlePause] Starting countdown (local state was waiting)`
          );
          this.startCountdown(499);
        } else {
          console.log(
            `[GameClient.handlePause] Ignoring pause packet while waiting (delayStart=${pausePacket.delayStart})`
          );
        }
        return;
      }

      console.log(
        `[GameClient.handlePause] Cancelling countdown (countdown=${this.state.countdown})`
      );
      this.state.countdown = 0;
      this.stopCountdown();
      this.state.status = "waiting";
      this.updateCallback(this.state);
      return;
    }

    const playerName =
      pausePacket.player === -1
        ? "Server"
        : Array.from(this.state.players.values()).find(
            (p: PlayerState) => p.numPlayer === pausePacket.player
          )?.name || "Unknown";
    console.log(
      `[GameClient.handlePause] Pausing game (paused by ${playerName})`
    );
    this.state.isPaused = true;
    this.updateCallback(this.state);
  };

  private handleDead = (packet: Packet): void => {
    const deadPacket = packet as DeadPacket;

    console.log(
      `[GameClient.handleDead] Received P_DEAD: numPlayer=${deadPacket.numPlayer}, thenGone=${deadPacket.thenGone}`
    );

    let localPlayer: PlayerState | undefined;
    for (const [, player] of this.state.players.entries()) {
      if (player.connectionId === this.state.localConnectionId) {
        localPlayer = player;
        break;
      }
    }
    if (localPlayer && localPlayer.numPlayer === deadPacket.numPlayer) {
      console.warn(
        `[GameClient.handleDead] Received P_DEAD for local player (numPlayer=${deadPacket.numPlayer}), ignoring (should never happen, server excludes sender)`
      );
      this.updateCallback(this.state);
      return;
    }

    if (this.remoteGameManager) {
      console.log(
        `[GameClient.handleDead] Routing P_DEAD to remote game for numPlayer=${deadPacket.numPlayer}`
      );
      this.remoteGameManager.processPacketForPlayer(
        deadPacket.numPlayer,
        deadPacket
      );
    }

    this.updateCallback(this.state);
  };

  private handleStampBlock = (packet: Packet): void => {
    const stampPacket = packet as StampBlockPacket;

    if (stampPacket.connectionId === this.state.localConnectionId) {
      if (this.queuePacketCallback) {
        this.queuePacketCallback(stampPacket);
      }
    } else if (this.remoteGameManager) {
      console.log(
        `[GameClient.handleStampBlock] Queuing stamp packet to remote game for numPlayer=${stampPacket.numPlayer}`
      );
      this.remoteGameManager.processPacketForPlayer(
        stampPacket.numPlayer,
        stampPacket
      );
    }
  };

  /**
   * Handle P_PLAYER packet (notifies about new player, matches original net_list.cc:1353-1363)
   * Reference: quadra/source/net_list.cc:1353-1363 (Net_list::check_player)
   * When a new player joins, existing clients receive P_PLAYER to create remote game for that player
   */
  private handlePlayer = (packet: Packet): void => {
    const playerPacket = packet as PlayerPacket;
    console.log(
      `[GameClient.handlePlayer] Received P_PLAYER: name=${playerPacket.name}, pos=${playerPacket.pos}, playerId=${playerPacket.playerId}, team=${playerPacket.team}, state currently has ${this.state.players.size} players:`,
      Array.from(this.state.players.entries()).map(
        ([id, p]) =>
          `connectionId=${id}, numPlayer=${p.numPlayer}, name=${p.name}`
      )
    );

    let existingPlayer: PlayerState | undefined;
    let existingConnectionId: string | undefined;

    for (const [, player] of this.state.players.entries()) {
      if (
        player.playerId === playerPacket.playerId ||
        player.numPlayer === playerPacket.pos
      ) {
        existingPlayer = player;
        existingConnectionId = player.connectionId;
        break;
      }
    }

    const connectionId =
      existingConnectionId ||
      `player_${playerPacket.playerId}_${playerPacket.pos}`;
    const playerState: PlayerState = existingPlayer
      ? {
          ...existingPlayer,
          connectionId: connectionId,
          name: playerPacket.name,
          numPlayer: playerPacket.pos,
          team: playerPacket.team,
          playerId: playerPacket.playerId,
          handicap: playerPacket.handicap,
          blockPack:
            playerPacket.blockPack || existingPlayer.blockPack || "default",
          canvasPack:
            playerPacket.canvasPack || existingPlayer.canvasPack || "default",
          fragAnnouncerPack:
            playerPacket.fragAnnouncerPack ||
            existingPlayer.fragAnnouncerPack ||
            "default",
        }
      : {
          connectionId: connectionId,
          name: playerPacket.name,
          numPlayer: playerPacket.pos,
          team: playerPacket.team,
          playerId: playerPacket.playerId,
          handicap: playerPacket.handicap,
          blockPack: playerPacket.blockPack || "default",
          canvasPack: playerPacket.canvasPack || "default",
          fragAnnouncerPack: playerPacket.fragAnnouncerPack || "default",
          board: this.createEmptyBoard(),
          score: 0,
          frags: 0,
          deaths: 0,
          isActive: true,
          state: "normal",
          level: this.state.params?.startLevel || 1,
          lines: 0,
          isAttacking: false,
          ppm: 0,
          bpm: 0,
        };

    this.setPlayer(playerState);

    if (this.state.gameSeed !== undefined) {
      if (!this.remoteGameManager) {
        this.remoteGameManager = new RemoteGameManager();
        console.log(
          `[GameClient.handlePlayer] Created RemoteGameManager synchronously (matches original Canvas creation)`
        );
      }

      const waitDownload = false;
      this.remoteGameManager.createOrUpdateRemoteGame(
        connectionId,
        playerState,
        this.state.gameSeed,
        waitDownload,
        this.state.gameMode
      );
      console.log(
        `[GameClient.handlePlayer] Created remote game synchronously for ${connectionId} (numPlayer=${playerPacket.pos}, waitDownload=false)`
      );
    } else {
      console.warn(
        `[GameClient.handlePlayer] Cannot create remote game: gameSeed not available yet for ${connectionId}`
      );
    }

    console.log(
      `[GameClient.handlePlayer] After adding player ${playerPacket.name} (numPlayer=${playerPacket.pos}), state now has ${this.state.players.size} players, calling ensureWatchingAllPlayers()...`
    );
    this.ensureWatchingAllPlayers();

    this.updateCallback(this.state);
  };

  /**
   * Handle P_SERVERRANDOM packet (new seed for round restart, matches original net_list.cc:343-349)
   * Reference: quadra/source/net_list.cc:343-349 (Packet_serverrandom handling)
   * Original queues packet via game->stackpacket() and processes in step_all()
   */
  private handleServerRandom = (packet: Packet): void => {
    const serverRandomPacket = packet as ServerRandomPacket;

    console.log(
      `[GameClient.handleServerRandom] Received P_SERVERRANDOM: seed=${serverRandomPacket.seed}`
    );

    if (this.queuePacketCallback) {
      this.queuePacketCallback(serverRandomPacket);
    }

    if (this.remoteGameManager) {
      const remoteGames = this.remoteGameManager.getAllRemoteGames();
      remoteGames.forEach((entry, numPlayer) => {
        if (entry.game.canvas) {
          const label =
            entry.connectionId !== undefined
              ? entry.connectionId
              : `numPlayer=${numPlayer}`;
          console.log(
            `[GameClient.handleServerRandom] Updating seed for remote game ${label}`
          );
          entry.game.canvas.setSeed(serverRandomPacket.seed);
        }
      });
    }

    this.state.gameSeed = serverRandomPacket.seed;
  };

  /**
   * Handle P_SERVERSTATE packet (periodic syncpoint updates, matches original net_list.cc:362-365)
   * Reference: quadra/source/net_list.cc:362 (Packet_serverstate handling)
   * Original queues packets via game->stackpacket() and processes every 5 frames
   */
  private handleServerState = (packet: Packet): void => {
    const serverStatePacket = packet as ServerStatePacket;

    const currentSyncpoint = this.state.roundSyncState;
    if (currentSyncpoint !== serverStatePacket.state) {
      console.log(
        `[GameClient] Syncpoint change: "${currentSyncpoint}" → "${serverStatePacket.state}"`
      );
    }
    
    // Update gameMessages from packet if present
    if (serverStatePacket.gameMessages && serverStatePacket.gameMessages.length > 0) {
      const existingMessages = this.state.gameMessages || [];
      // Merge new messages, avoiding duplicates
      const mergedMessages = [...existingMessages];
      serverStatePacket.gameMessages.forEach((newMsg: GameMessageEvent) => {
        if (!existingMessages.find((m: GameMessageEvent) => m.id === newMsg.id)) {
          mergedMessages.push(newMsg);
        }
      });
      // Trim to 50 messages
      if (mergedMessages.length > 50) {
        mergedMessages.splice(0, mergedMessages.length - 50);
      }
      this.state.gameMessages = mergedMessages;
      // Trigger update callback to notify React components
      this.updateCallback(this.state);
    }
    
    this.queuedServerStatePackets.push(serverStatePacket);
  };

  /**
   * Handle incoming game message packet
   * Processes message immediately and updates state for React layer
   */
  private handleGameMessage = (packet: Packet): void => {
    const gameMessagePacket = packet as GameMessagePacket;
    const message = gameMessagePacket.message;
    
    // Update state with message (for React layer consumption)
    // Create a new array to ensure React detects the change
    const existingMessages = this.state.gameMessages || [];
    
    // Deduplicate by ID (in case of network retransmission)
    if (!existingMessages.find((m: GameMessageEvent) => m.id === message.id)) {
      // Create new array with the new message
      let gameMessages = [...existingMessages, message];
      
      // Trim array if it exceeds 50 messages (prevent memory growth)
      if (gameMessages.length > 50) {
        gameMessages = gameMessages.slice(-50);
      }
      
      // Create new state object to ensure React detects the change
      this.state = {
        ...this.state,
        gameMessages: gameMessages,
      };
      this.updateCallback(this.state);
    }
  };

  private handlePlayerCanvasState = (packet: Packet): void => {
    const canvasPacket = packet as PlayerCanvasStatePacket;
    const playerEntry = this.state.players.get(canvasPacket.numPlayer);

    if (playerEntry) {
      const updatedPlayer: PlayerState = {
        ...playerEntry,
        canvasState: canvasPacket.canvasState,
      };
      this.state.players.set(canvasPacket.numPlayer, updatedPlayer);
      if (updatedPlayer.connectionId) {
        this.state.connections.set(updatedPlayer.connectionId, updatedPlayer);
      }
    }

    if (this.remoteGameManager) {
      this.remoteGameManager.updateCanvasState(
        canvasPacket.numPlayer,
        canvasPacket.canvasState
      );
    }

    this.updateCallback(this.state);
  };

  /**
   * Process queued P_SERVERSTATE packets with frame delay (matches original net_list.cc:359-371)
   * Should be called from game loop every frame
   * Reference: quadra/source/net_list.cc:359-371 (syncframes counter)
   */
  public processQueuedServerStatePackets(): void {
    if (this.syncframes >= 5) {
      if (this.queuedServerStatePackets.length > 0) {
        console.log(
          `[GameClient.processQueuedServerStatePackets] Processing packet, syncframes=${this.syncframes}, queue length=${this.queuedServerStatePackets.length}`
        );
      }
      if (this.queuedServerStatePackets.length > 0) {
        let serverStatePacket: ServerStatePacket | null = null;
        while (this.queuedServerStatePackets.length > 0) {
          const candidate = this.queuedServerStatePackets.shift()!;
          if (candidate.state !== this.state.roundSyncState) {
            serverStatePacket = candidate;
            break;
          }
        }

        if (serverStatePacket) {
          const previousSyncpoint = this.state.roundSyncState;

          console.log(
            `[GameClient.processQueuedServerStatePackets] Processing packet: current="${previousSyncpoint}", packet="${serverStatePacket.state}"`
          );

          this.state.roundSyncState = serverStatePacket.state;
          
          // Update gameMessages from packet if present
          if (serverStatePacket.gameMessages && serverStatePacket.gameMessages.length > 0) {
            const existingMessages = this.state.gameMessages || [];
            const mergedMessages = [...existingMessages];
            serverStatePacket.gameMessages.forEach((newMsg: GameMessageEvent) => {
              if (!existingMessages.find((m: GameMessageEvent) => m.id === newMsg.id)) {
                mergedMessages.push(newMsg);
              }
            });
            // Trim to 50 messages
            if (mergedMessages.length > 50) {
              mergedMessages.splice(0, mergedMessages.length - 50);
            }
            this.state.gameMessages = mergedMessages;
          }

          console.log(
            `[GameClient] ✅ Syncpoint updated: "${previousSyncpoint}" → "${serverStatePacket.state}" (queue length: ${this.queuedServerStatePackets.length})`
          );

          this.updateCallback(this.state);

          if (
            serverStatePacket.state === "playing" &&
            previousSyncpoint === "waitForRestart"
          ) {
            const localPlayer = this.getPlayerByConnectionId(
              this.state.localConnectionId
            );
            if (localPlayer) {
              const updatedPlayer: PlayerState = {
                ...localPlayer,
                isActive: true,
                state: "normal" as const,
                board: this.createEmptyBoard(),
                lines: 0,
                level: this.state.params?.startLevel || 1,
                lastAttackerNumPlayer: undefined,
                attackCounts: {},
                score: localPlayer.score || 0,
                roundWins: localPlayer.roundWins || 0,
                frags: localPlayer.frags || 0,
                deaths: localPlayer.deaths || 0,
                ppm: 0,
                bpm: 0,
              };
              this.setPlayer(updatedPlayer);
            }

            this.state.gameMessages = [];
          }
        }

        this.syncframes = 0;
      } else {
        this.syncframes = 0;
      }
    } else {
      this.syncframes++;
    }
  }

  private startCountdown(initialValue?: number): void {
    if (typeof initialValue === "number") {
      this.state.countdown = initialValue;
    }

    if (
      !this.state.countdown ||
      this.state.countdown === 0 ||
      this.state.countdown === 500
    ) {
      return;
    }

    this.stopCountdown();

    this.state.countdownStartTime = Date.now();
    this.state.status = "starting";
    this.state.isPaused = true;

    this.countdownInterval = setInterval(() => {
      if (
        !this.state.countdown ||
        this.state.countdown === 0 ||
        this.state.countdown === 500
      ) {
        this.stopCountdown();
        return;
      }

      this.state.countdown = Math.max(0, this.state.countdown - 1);

      if (this.state.countdown === 0) {
        this.state.isPaused = false;
        this.state.status = "active";
        this.stopCountdown();
        this.updateCallback(this.state);
        return;
      }

      this.updateCallback(this.state);
    }, this.COUNTDOWN_INTERVAL_MS);

    this.updateCallback(this.state);
  }

  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = undefined;
    }
    this.state.countdownStartTime = undefined;
  }

  private sendPlayerStateToServer(playerState: Partial<PlayerState>): void {
    const hasChanges = this.hasStateChanges(playerState);

    if (!hasChanges) {
      return;
    }

    const now = Date.now();
    const timeSinceLastSend = now - this.lastStateSendTime;

    if (this.pendingStateUpdate) {
      this.pendingStateUpdate = {
        ...this.pendingStateUpdate,
        ...playerState,

        board: playerState.board || this.pendingStateUpdate.board,
      };
    } else {
      this.pendingStateUpdate = { ...playerState };
    }

    if (timeSinceLastSend >= this.STATE_SEND_INTERVAL) {
      this.flushPendingStateUpdate();
    } else {
      const remainingTime = this.STATE_SEND_INTERVAL - timeSinceLastSend;
      if (!this.stateSendTimeout) {
        this.stateSendTimeout = setTimeout(() => {
          this.flushPendingStateUpdate();
        }, remainingTime);
      }
    }
  }

  private hasStateChanges(playerState: Partial<PlayerState>): boolean {
    if (!this.lastSentState) {
      return true;
    }

    if (
      playerState.score !== undefined &&
      playerState.score !== this.lastSentState.score
    ) {
      return true;
    }

    if (
      playerState.lines !== undefined &&
      playerState.lines !== this.lastSentState.lines
    ) {
      return true;
    }

    if (
      playerState.level !== undefined &&
      playerState.level !== this.lastSentState.level
    ) {
      return true;
    }

    if (
      playerState.isActive !== undefined &&
      playerState.isActive !== this.lastSentState.isActive
    ) {
      return true;
    }

    if (
      playerState.ppm !== undefined &&
      playerState.ppm !== this.lastSentState.ppm
    ) {
      return true;
    }

    if (
      playerState.bpm !== undefined &&
      playerState.bpm !== this.lastSentState.bpm
    ) {
      return true;
    }

    if (playerState.board?.currentPiece) {
      const lastPiece = this.lastSentState.board?.currentPiece;
      if (!lastPiece) {
        return true;
      }

      if (
        playerState.board.currentPiece.x !== lastPiece.x ||
        playerState.board.currentPiece.y !== lastPiece.y ||
        playerState.board.currentPiece.rotation !== lastPiece.rotation ||
        playerState.board.currentPiece.type !== lastPiece.type
      ) {
        return true;
      }
    } else if (this.lastSentState.board?.currentPiece) {
      return true;
    }

    return false;
  }

  private stateSendTimeout?: NodeJS.Timeout;

  private flushPendingStateUpdate(): void {
    if (!this.pendingStateUpdate) return;

    const playerState = this.pendingStateUpdate;
    this.pendingStateUpdate = null;
    this.lastStateSendTime = Date.now();

    if (this.stateSendTimeout) {
      clearTimeout(this.stateSendTimeout);
      this.stateSendTimeout = undefined;
    }

    const statePacket = new PlayerStatePacket();
    statePacket.connectionId = this.state.localConnectionId;

    if (playerState.board) {
      statePacket.boardUpdate = playerState.board;
    }

    if (playerState.score !== undefined) {
      statePacket.scoreUpdate = playerState.score;
    }
    if (playerState.lines !== undefined) {
      statePacket.linesUpdate = playerState.lines;
    }
    if (playerState.level !== undefined) {
      statePacket.levelUpdate = playerState.level;
    }

    if (playerState.ppm !== undefined) {
      statePacket.ppmUpdate = playerState.ppm;
    }
    if (playerState.bpm !== undefined) {
      statePacket.bpmUpdate = playerState.bpm;
    }
    if (playerState.isActive !== undefined) {
      statePacket.state = playerState.isActive ? "normal" : "dead";
    }

    this.socket.send(statePacket);

    this.lastSentState = { ...playerState };
  }

  private keyCodeToMoveType(keyCode: number): MoveType | null {
    switch (keyCode) {
      case 37:
        return MoveType.MOVE_LEFT;
      case 39:
        return MoveType.MOVE_RIGHT;
      case 40:
        return MoveType.MOVE_DOWN;
      case 38:
        return MoveType.ROTATE_CW;
      case 32:
        return MoveType.DROP;
      case 90:
        return MoveType.ROTATE_CCW;
      case 67:
        return MoveType.HOLD;
      default:
        return null;
    }
  }

  private createInitialState(): GameState {
    return {
      players: new Map(),
      connections: new Map(),
      gameSeed: 0,
      isPaused: false,
      gameMode: "standard",
      isServer: false,
      localConnectionId: "",
      status: "waiting",
      startTime: 0,
      countdown: undefined,
      countdownStartTime: undefined,
    };
  }

  /**
   * Sync connections map from players map (for networking lookups by connectionId)
   */
  private syncConnections(): void {
    this.state.connections.clear();
    for (const [, player] of this.state.players.entries()) {
      if (player.connectionId) {
        this.state.connections.set(player.connectionId, player);
      }
    }
  }

  /**
   * Get player by connectionId (for networking lookups)
   */
  private getPlayerByConnectionId(
    connectionId: string
  ): PlayerState | undefined {
    return this.state.connections.get(connectionId);
  }

  /**
   * Get player by numPlayer (for game logic, matches original list[MAXPLAYERS])
   */
  private getPlayerByNumPlayer(numPlayer: number): PlayerState | undefined {
    return this.state.players.get(numPlayer);
  }

  /**
   * Set player in both players (by numPlayer) and connections (by connectionId)
   */
  private setPlayer(player: PlayerState): void {
    if (
      player.numPlayer !== undefined &&
      player.numPlayer >= 0 &&
      player.numPlayer <= 5
    ) {
      this.state.players.set(player.numPlayer, player);
      if (player.connectionId) {
        this.state.connections.set(player.connectionId, player);
      }
    }
  }

  /**
   * Delete player by numPlayer
   */
  private deletePlayerByNumPlayer(numPlayer: number): void {
    const player = this.state.players.get(numPlayer);
    if (player) {
      this.state.players.delete(numPlayer);
      if (player.connectionId) {
        this.state.connections.delete(player.connectionId);
      }
    }
  }

  /**
   * Delete player by connectionId
   */
  private deletePlayerByConnectionId(connectionId: string): void {
    const player = this.state.connections.get(connectionId);
    if (player && player.numPlayer !== undefined) {
      this.state.players.delete(player.numPlayer);
      this.state.connections.delete(connectionId);
    }
  }

  private createEmptyBoard(): BoardState {
    const emptyGrid = Array(20)
      .fill(null)
      .map(() => Array(10).fill(0));

    return {
      grid: emptyGrid,
      width: 10,
      height: 20,
      clearingLines: [],
      pendingGarbage: [],
    };
  }

  /**
   * Set countdown value (for local countdown decrement)
   * Matches original: each client decrements its own delay_start
   */
  setCountdown(countdown: number | undefined): void {
    this.state.countdown = countdown;
    if (countdown && countdown !== 0 && countdown !== 500) {
      this.startCountdown(countdown);
    } else {
      if (countdown === 500) {
        this.state.status = "waiting";
        this.state.isPaused = true;
      }
      this.stopCountdown();
      this.updateCallback(this.state);
    }
  }

  /**
   * Set game status (for local countdown completion)
   */
  setGameStatus(status: "waiting" | "starting" | "active" | "finished"): void {
    this.state.status = status;
    if (status === "active") {
      this.state.startTime = Date.now();
      this.state.countdown = undefined;
    }
    this.updateCallback(this.state);
  }

  /**
   * Set paused state (for local countdown completion)
   */
  setPaused(paused: boolean): void {
    this.state.isPaused = paused;
    this.updateCallback(this.state);
  }

  public updateGameOptions(options: Partial<GameOptions>): void {
    const packet = new GameSettingsPacket();
    packet.settings = {
        gameEndType: options.gameEndType || "none",
        gameEndValue: options.gameEndValue || 0
    };
    this.socket.send(packet);
  }

  public sendStampBlock(lines: any[]): void {
      console.warn("[GameClient] sendStampBlock (array) not implemented");
  }

  public sendStampBlockArgs(x: number, y: number, rotation: number, blockType: number): void {
    if (!this.state.localConnectionId) return;

    const localPlayer = this.getPlayerByConnectionId(this.state.localConnectionId);
    if (!localPlayer) return;

    const packet = new ClientStampBlockPacket();
    packet.connectionId = this.state.localConnectionId;
    packet.numPlayer = localPlayer.numPlayer;
    packet.x = x;
    packet.y = y;
    packet.rotation = rotation;
    // Defaults for fields not passed by adapter
    packet.score = 0;
    packet.date = 0;
    packet.blockRotated = 0;
    packet.timeHeld = 0;

    this.socket.send(packet);
  }
}
